// frontend/src/app/services/merchant-period.service.ts
import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export type PeriodType =
  | 'vacation'
  | 'reduced_staff'
  | 'closure'
  | 'high_season'
  | 'custom';

export interface MerchantPeriod {
  _id: string;
  userId: string;
  label: string;
  type: PeriodType;
  startDate: string; // ISO date
  endDate: string; // ISO date
  color: string | null;
  notes: string;
  reductionCoeff: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePeriodPayload {
  label: string;
  type: PeriodType;
  startDate: string;
  endDate: string;
  color?: string | null;
  notes?: string;
  reductionCoeff?: number | null;
}

/** Icône et label pour chaque type de période */
export const PERIOD_TYPE_META: Record<
  PeriodType,
  { icon: string; label: string; defaultColor: string }
> = {
  vacation: { icon: '🏖️', label: 'Congés', defaultColor: '#4ade80' },
  reduced_staff: {
    icon: '👥',
    label: 'Effectifs réduits',
    defaultColor: '#fb923c',
  },
  closure: { icon: '🔒', label: 'Fermeture', defaultColor: '#f87171' },
  high_season: { icon: '🔥', label: 'Haute saison', defaultColor: '#facc15' },
  custom: { icon: '✏️', label: 'Personnalisé', defaultColor: '#a78bfa' },
};

@Injectable({ providedIn: 'root' })
export class MerchantPeriodService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/merchant-periods`;

  /** Signal — liste des périodes chargées */
  readonly periods = signal<MerchantPeriod[]>([]);

  /**
   * Charger les périodes pour une plage de dates (optionnel).
   */
  load(start?: Date, end?: Date): Observable<{ periods: MerchantPeriod[] }> {
    let params = new HttpParams();
    if (start) params = params.set('start', start.toISOString());
    if (end) params = params.set('end', end.toISOString());

    return this.http
      .get<{ periods: MerchantPeriod[] }>(this.baseUrl, { params })
      .pipe(tap((res) => this.periods.set(res.periods)));
  }

  create(payload: CreatePeriodPayload): Observable<{ period: MerchantPeriod }> {
    return this.http
      .post<{ period: MerchantPeriod }>(this.baseUrl, payload)
      .pipe(tap((res) => this.periods.update((list) => [...list, res.period])));
  }

  update(
    id: string,
    payload: Partial<CreatePeriodPayload>,
  ): Observable<{ period: MerchantPeriod }> {
    return this.http
      .put<{ period: MerchantPeriod }>(`${this.baseUrl}/${id}`, payload)
      .pipe(
        tap((res) =>
          this.periods.update((list) =>
            list.map((p) => (p._id === id ? res.period : p)),
          ),
        ),
      );
  }

  delete(id: string): Observable<{ message: string }> {
    return this.http
      .delete<{ message: string }>(`${this.baseUrl}/${id}`)
      .pipe(
        tap(() =>
          this.periods.update((list) => list.filter((p) => p._id !== id)),
        ),
      );
  }

  /** Trie par priorité (closure > vacation > reduced_staff > ...) */
  private sortByPriority(list: MerchantPeriod[]): MerchantPeriod[] {
    const priority: Record<PeriodType, number> = {
      closure: 4,
      vacation: 3,
      reduced_staff: 2,
      high_season: 1,
      custom: 0,
    };
    return list
      .slice()
      .sort((a, b) => (priority[b.type] ?? 0) - (priority[a.type] ?? 0));
  }

  /**
   * Retourne toutes les périodes actives pour une date donnée, triées par priorité.
   */
  getAllActiveForDate(date: Date): MerchantPeriod[] {
    const day = date.toISOString().slice(0, 10);
    const actives = this.periods().filter(
      (p) => p.startDate.slice(0, 10) <= day && p.endDate.slice(0, 10) >= day,
    );
    return this.sortByPriority(actives);
  }

  /**
   * Retourne la période active la plus restrictive pour une date donnée.
   */
  getActiveForDate(date: Date): MerchantPeriod | null {
    const all = this.getAllActiveForDate(date);
    return all.length ? all[0] : null;
  }
}
